import { useEffect } from "react";

import appConfig from "@config/app";
import { useRegisterDevice } from "hassibot/services_v2/login/login.queries";
import { serviceWorkerRegistration } from "hassibot/services_v2/service-worker";
import { mtLogger, urlB64ToUint8Array } from "shared-core";

const getOrCreateSubscription = async (): Promise<{
  subscription: PushSubscription | undefined;
  isFreshSubscription: boolean;
}> => {
  if (!serviceWorkerRegistration) {
    return { subscription: undefined, isFreshSubscription: false };
  }

  if (!import.meta.env.VITE_VAPID_PUBLIC) {
    mtLogger.error("There is not application server key");
    return { subscription: undefined, isFreshSubscription: false };
  }

  if (!("PushManager" in window)) {
    mtLogger.error("Push notifications are not supported");
    return { subscription: undefined, isFreshSubscription: false };
  }

  const registration = await serviceWorkerRegistration;

  const applicationServerKey = urlB64ToUint8Array(import.meta.env.VITE_VAPID_PUBLIC);

  const currentSubscription = await registration.pushManager.getSubscription();

  if (currentSubscription) {
    // Does the PushToken match our application server key ?
    if (
      currentSubscription.options.applicationServerKey &&
      new Uint8Array(currentSubscription.options.applicationServerKey).toString() ===
        applicationServerKey.toString()
    ) {
      mtLogger.debug("PushSubscription is active");
      return { subscription: currentSubscription, isFreshSubscription: false };
    }

    mtLogger.debug("An invalid PushSubscription is active, unsubscribing it");
    await currentSubscription.unsubscribe();
  }

  const subscribeOptions = {
    userVisibleOnly: true,
    applicationServerKey,
  };
  let newSubscription: PushSubscription | undefined;
  try {
    newSubscription = await registration.pushManager.subscribe(subscribeOptions);
  } catch {
    mtLogger.warn("PushSubscription is disabled (probably a permission denied)");
    return { subscription: undefined, isFreshSubscription: false };
  }

  return { subscription: newSubscription, isFreshSubscription: true };
};

/**
 * Hook that registers the device and manages push notification subscriptions.
 * This should be called at application startup after authentication.
 */
export const useDeviceRegistration = (): void => {
  const { mutateAsync: registerDeviceMutation } = useRegisterDevice();

  useEffect(() => {
    const updatePushToken = async (withRetry = true): Promise<void> => {
      try {
        const { subscription } = await getOrCreateSubscription();
        const pushToken = subscription ? JSON.stringify(subscription) : null;
        const { isPushTokenValid } = await registerDeviceMutation([appConfig.DEVICE_ID, pushToken]);

        if (subscription && !isPushTokenValid && withRetry) {
          // We might have an invalid current subscription. Let's try to
          // unsubscribe/resubscribe once.
          await subscription.unsubscribe();
          await updatePushToken(false);
        }
      } catch (error) {
        // Non-critical - push notifications may not work but app continues
        console.error("Failed to update push token:", error);
      }
    };

    // Run immediately on mount
    updatePushToken();
  }, [registerDeviceMutation]);
};
